import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import type { FilterAst } from "../../core/filter";
import { COMPUTED_KEYS, computeAll } from "../../core/computed";
import type { Task } from "../../core/schema";
import { groupsOf } from "../../core/structure";
import { FILTER_SCHEMA, visibleTasks } from "../shared";
import { colorKeys, cssColour, hasWebGL, project, type Point } from "./geometry";
import { AXIS_DEFAULTS, dotColour } from "./axisColor";
import { AxisLegend } from "./AxisLegend";
import { axisRange } from "./range";
import type { SceneApi } from "./scene3d";

export interface Axis3 {
  prop: string;
  min: number;
  max: number;
  label?: string;
}

/** Flat keys like `eisenhower2` (the config form has no nested objects). */
export interface Eisenhower3Config {
  filter?: FilterAst;
  xProp?: string;
  xMin?: number;
  xMax?: number;
  xLabel?: string;
  yProp?: string;
  yMin?: number;
  yMax?: number;
  yLabel?: string;
  zProp?: string;
  zMin?: number;
  zMax?: number;
  zLabel?: string;
  /** "fit to data" per axis: from the smallest to the largest value shown */
  xFit?: boolean;
  yFit?: boolean;
  zFit?: boolean;
  colorBy?: "axes" | "group" | "type" | "none";
  xColorMin?: string;
  xColorMax?: string;
  yColorMin?: string;
  yColorMax?: string;
  zColorMin?: string;
  zColorMax?: string;
}

const DEFAULTS = {
  xProp: "energy",
  xMin: 1,
  xMax: 5,
  yProp: "want",
  yMin: 0,
  yMax: 3,
  zProp: "estimateMin",
  zMin: 0,
  zMax: 120,
} satisfies Eisenhower3Config;

function axes(c: Eisenhower3Config): { x: Axis3; y: Axis3; z: Axis3 } {
  const a = (k: "x" | "y" | "z"): Axis3 => ({
    prop: c[`${k}Prop`] ?? DEFAULTS[`${k}Prop`],
    min: c[`${k}Min`] ?? DEFAULTS[`${k}Min`],
    max: c[`${k}Max`] ?? DEFAULTS[`${k}Max`],
    label: c[`${k}Label`],
  });
  return { x: a("x"), y: a("y"), z: a("z") };
}

const isComputed = (p: string) => (COMPUTED_KEYS as readonly string[]).includes(p);

interface Raw {
  id: string;
  title: string;
  v: [number, number, number];
  key: string;
}

/**
 * Three-axis scatter (spec §9.7 `eisenhower3`): any numeric or computed prop per axis, orbit/zoom, click
 * opens the task; no dragging. three.js is loaded only when the widget is shown; without WebGL the same
 * data is drawn as a rotatable 2.5D projection. A list of the points is always in the DOM for screen
 * readers and keyboards.
 */
export function Eisenhower3View({ config, ctx }: ViewProps<Eisenhower3Config>) {
  const { t } = useTranslation();
  const ax = axes(config);
  // plain colours (a11y) win over axis colours: then the group colours or one muted hue
  const plain = document.documentElement.dataset.plainColors === "true";
  const colorBy = config.colorBy ?? "axes";
  const minute = Math.floor(ctx.now / 60_000) * 60_000;
  const values = useDocSelector(
    ctx.handle,
    (doc): Raw[] => {
      const v = visibleTasks(doc, config.filter, minute);
      const val = (task: Task, prop: string): number | null => {
        const raw = isComputed(prop)
          ? (computeAll(v.snapshot, task.id, minute) as Record<string, unknown>)[prop]
          : task.props[prop];
        return typeof raw === "number" ? raw : typeof raw === "boolean" ? (raw ? 1 : 0) : null;
      };
      const out: Raw[] = [];
      for (const task of v.tasks) {
        const xs = [val(task, ax.x.prop), val(task, ax.y.prop), val(task, ax.z.prop)];
        if (xs.some((n) => n === null)) continue;
        const key =
          colorBy === "group"
            ? (groupsOf(v.snapshot, task.id).sort()[0] ?? "")
            : colorBy === "type"
              ? task.typeId
              : "";
        out.push({ id: task.id, title: task.title, v: xs as [number, number, number], key });
      }
      return out;
    },
    [config, minute],
  );
  // the axes' ranges: as set, or fitted to the values shown
  const raw = useMemo((): Point[] => {
    const list = values ?? [];
    const r = (["x", "y", "z"] as const).map((k, i) =>
      axisRange(
        list.map((p) => p.v[i]),
        config[`${k}Fit`],
        ax[k].min,
        ax[k].max,
      ),
    );
    const norm = (n: number, i: number) =>
      Math.min(1, Math.max(0, (n - r[i].min) / (r[i].max - r[i].min || 1)));
    return list.map((p) => ({
      id: p.id,
      title: p.title,
      p: [norm(p.v[0], 0), norm(p.v[1], 1), norm(p.v[2], 2)],
      color: "",
      key: p.key,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values,
    config.xFit,
    config.yFit,
    config.zFit,
    ax.x.min,
    ax.x.max,
    ax.y.min,
    ax.y.max,
    ax.z.min,
    ax.z.max,
  ]);
  const axisColours = useMemo(
    () =>
      colorBy === "axes" && !plain
        ? (["x", "y", "z"] as const).map((k) => ({
            low: config[`${k}ColorMin`] ?? AXIS_DEFAULTS[k].low,
            high: config[`${k}ColorMax`] ?? AXIS_DEFAULTS[k].high,
          }))
        : null,
    [colorBy, plain, config],
  );
  const colored = useMemo(() => {
    const points = raw ?? [];
    if (axisColours)
      return points.map((p) => ({ ...p, color: dotColour(axisColours, [...p.p]) ?? "--chart-seq" }));
    const keys = colorKeys(points.map((p) => p.key));
    return points.map((p) => ({ ...p, color: keys.get(p.key) ?? "--chart-other" }));
  }, [raw, axisColours]);
  const [webgl] = useState(hasWebGL);
  const lx = ax.x.label || ax.x.prop;
  const ly = ax.y.label || ax.y.prop;
  const lz = ax.z.label || ax.z.prop;
  const labels = useMemo(() => [lx, ly, lz] as const, [lx, ly, lz]);

  return (
    <div
      className="kss-surface relative h-full p-2"
      data-testid="eisenhower3-view"
      data-renderer={webgl ? "webgl" : "svg"}
    >
      {webgl ? (
        <Scene points={colored} labels={labels} onPick={ctx.openTask} />
      ) : (
        <Projection points={colored} labels={labels} onPick={ctx.openTask} />
      )}
      {axisColours && (
        <div className="absolute bottom-2 left-2 right-2 rounded-sm bg-[var(--surface)]/80 py-0.5">
          <AxisLegend
            axes={[
              { label: lx, ...axisColours[0] },
              { label: ly, ...axisColours[1] },
              { label: lz, ...axisColours[2] },
            ]}
          />
        </div>
      )}
      <ul className="sr-only" aria-label={t("views.eisenhower3.label")}>
        {colored.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => ctx.openTask(p.id)}>
              {t("views.eisenhower3.point", {
                title: p.title,
                x: labels[0],
                xv: p.p[0].toFixed(2),
                y: labels[1],
                yv: p.p[1].toFixed(2),
                z: labels[2],
                zv: p.p[2].toFixed(2),
              })}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

type SceneProps = { points: Point[]; labels: readonly [string, string, string]; onPick(id: string): void };

function Scene({ points, labels, onPick }: SceneProps) {
  const host = useRef<HTMLDivElement>(null);
  const [scene, setScene] = useState<SceneApi | null>(null);
  const [tags, setTags] = useState<{ text: string; left: number; top: number }[]>([]);

  useEffect(() => {
    let alive = true;
    let made: SceneApi | null = null;
    void import("./scene3d").then(async ({ createScene }) => {
      const el = host.current;
      if (!alive || !el) return;
      made = await createScene(el, {
        reducedMotion: document.documentElement.dataset.reducedMotion === "true",
        onLabels: setTags,
      });
      if (alive) setScene(made);
      else made.dispose();
    });
    return () => {
      alive = false;
      made?.dispose();
    };
  }, []);

  useEffect(() => {
    scene?.update(points, labels, onPick);
  }, [scene, points, labels, onPick]);

  return (
    <div ref={host} className="relative h-full w-full cursor-grab" data-testid="eisenhower3-canvas">
      {tags.map((tag) => (
        <span
          key={tag.text}
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 font-mono text-[11px] text-neutral-500"
          style={{ left: tag.left, top: tag.top }}
        >
          {tag.text}
        </span>
      ))}
    </div>
  );
}

function Projection({ points, labels, onPick }: SceneProps) {
  const { t } = useTranslation();
  const [view, setView] = useState({ yaw: 0.7, pitch: 0.45 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const S = 300;
  const to = (p: [number, number, number]) => {
    const [x, y, z] = project(p, view.yaw, view.pitch);
    const k = 1 / (1.8 - z * 0.4);
    return { x: S / 2 + x * S * 0.6 * k, y: S / 2 - y * S * 0.6 * k, z };
  };
  const corners: [number, number, number][] = [
    [0, 0, 0],
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
    [1, 1, 0],
    [1, 0, 1],
    [0, 1, 1],
    [1, 1, 1],
  ];
  const edges = [
    [0, 1],
    [0, 2],
    [0, 3],
    [1, 4],
    [1, 5],
    [2, 4],
    [2, 6],
    [3, 5],
    [3, 6],
    [4, 7],
    [5, 7],
    [6, 7],
  ];
  const ends: [number, number, number][] = [
    [1.1, 0, 0],
    [0, 1.1, 0],
    [0, 0, 1.1],
  ];
  const sorted = [...points].sort((a, b) => to(a.p).z - to(b.p).z);
  return (
    <svg
      viewBox={`0 0 ${S} ${S}`}
      className="h-full w-full touch-none select-none"
      role="group"
      aria-label={t("views.eisenhower3.label")}
      tabIndex={0}
      onKeyDown={(e) => {
        const step = 0.1;
        if (e.key === "ArrowLeft") setView((v) => ({ ...v, yaw: v.yaw - step }));
        if (e.key === "ArrowRight") setView((v) => ({ ...v, yaw: v.yaw + step }));
        if (e.key === "ArrowUp") setView((v) => ({ ...v, pitch: Math.max(-1.2, v.pitch - step) }));
        if (e.key === "ArrowDown") setView((v) => ({ ...v, pitch: Math.min(1.2, v.pitch + step) }));
      }}
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const dx = e.clientX - drag.current.x;
        const dy = e.clientY - drag.current.y;
        drag.current = { x: e.clientX, y: e.clientY };
        setView((v) => ({
          yaw: v.yaw + dx * 0.01,
          pitch: Math.max(-1.2, Math.min(1.2, v.pitch + dy * 0.01)),
        }));
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
      data-testid="eisenhower3-svg"
    >
      {edges.map(([a, b]) => {
        const p = to(corners[a]);
        const q = to(corners[b]);
        return <line key={`${a}-${b}`} x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke="var(--line)" />;
      })}
      {ends.map((e, i) => {
        const p = to(e);
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            fontSize="10"
            textAnchor="middle"
            fill="var(--color-neutral-500)"
            fontFamily="var(--font-mono)"
          >
            {labels[i]}
          </text>
        );
      })}
      {sorted.map((pt) => {
        const p = to(pt.p);
        return (
          <circle
            key={pt.id}
            cx={p.x}
            cy={p.y}
            r={4 + p.z * 2}
            fill={cssColour(pt.color)}
            stroke="var(--surface)"
            strokeWidth={2}
            className="cursor-pointer"
            onClick={() => onPick(pt.id)}
          >
            <title>{pt.title}</title>
          </circle>
        );
      })}
    </svg>
  );
}

const colour: import("../../dashboards/ConfigForm").JsonSchema = { type: "string", "x-drang": "color" };

const numProp: import("../../dashboards/ConfigForm").JsonSchema = {
  type: "string",
  "x-drang": "prop",
  "x-kinds": ["number"],
  "x-computed": true,
};
registerViewType<Eisenhower3Config>({
  id: "eisenhower3",
  label: "views.eisenhower3.label",
  icon: "box",
  level: "power",
  defaultConfig: { ...DEFAULTS, colorBy: "axes" },
  configSchema: {
    type: "object",
    properties: {
      xProp: { ...numProp, title: "config.xProp" },
      xMin: { type: "number", title: "config.xMin" },
      xMax: { type: "number", title: "config.xMax" },
      xFit: { type: "boolean", title: "config.fitToData" },
      xLabel: { type: "string", title: "config.xLabel" },
      yProp: { ...numProp, title: "config.yProp" },
      yMin: { type: "number", title: "config.yMin" },
      yMax: { type: "number", title: "config.yMax" },
      yFit: { type: "boolean", title: "config.fitToData" },
      yLabel: { type: "string", title: "config.yLabel" },
      zProp: { ...numProp, title: "config.zProp" },
      zMin: { type: "number", title: "config.zMin" },
      zMax: { type: "number", title: "config.zMax" },
      zFit: { type: "boolean", title: "config.fitToData" },
      zLabel: { type: "string", title: "config.zLabel" },
      colorBy: { type: "string", title: "config.colorBy", enum: ["axes", "group", "type", "none"] },
      xColorMin: { ...colour, default: AXIS_DEFAULTS.x.low, title: "config.xColorMin" },
      xColorMax: { ...colour, default: AXIS_DEFAULTS.x.high, title: "config.xColorMax" },
      yColorMin: { ...colour, default: AXIS_DEFAULTS.y.low, title: "config.yColorMin" },
      yColorMax: { ...colour, default: AXIS_DEFAULTS.y.high, title: "config.yColorMax" },
      zColorMin: { ...colour, default: AXIS_DEFAULTS.z.low, title: "config.zColorMin" },
      zColorMax: { ...colour, default: AXIS_DEFAULTS.z.high, title: "config.zColorMax" },
      filter: FILTER_SCHEMA,
    },
  },
  Component: Eisenhower3View,
  minSize: { w: 4, h: 5 },
});
